import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Clock, Loader, Trash2, RotateCw } from 'lucide-react';
import C from '../../constants/colors';
import * as historyApi from '../../api/history';

const ACCEPTED_EXT = ['.pdf', '.docx'];

function statusChip(status) {
  const map = {
    PENDING:    { color: C.amber,    icon: Clock,        label: 'Pending'    },
    PROCESSING: { color: C.blue,     icon: Loader,       label: 'Processing' },
    INDEXED:    { color: C.green,    icon: CheckCircle,  label: 'Indexed'    },
    FAILED:     { color: C.red,      icon: AlertCircle,  label: 'Failed'     },
  };
  const m = map[status] || map.PENDING;
  const Icon = m.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: m.color, fontSize: 12, fontWeight: 600 }}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

export default function UploadManagerView({ sites }) {
  const [docs, setDocs] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState([]); // [{ fileName, status: 'uploading'|'error', error? }]
  const [scopeSiteId, setScopeSiteId] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteText, setPasteText] = useState('');
  const fileInput = useRef(null);

  const refresh = useCallback(async () => {
    try { setDocs(await historyApi.listDocs()); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 3s while any doc is PENDING or PROCESSING
    const poll = setInterval(() => {
      if (docs.some(d => d.status === 'PENDING' || d.status === 'PROCESSING')) refresh();
    }, 3000);
    return () => clearInterval(poll);
  }, [docs, refresh]);

  async function uploadOne(file, title) {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (scopeSiteId) fd.append('siteId', scopeSiteId);
    return historyApi.uploadDoc(fd);
  }

  async function handleFiles(files) {
    const arr = [...files];
    const initial = arr.map(f => ({ fileName: f.name, status: 'uploading' }));
    setUploading(initial);

    for (let i = 0; i < arr.length; i++) {
      try {
        await uploadOne(arr[i]);
        setUploading(prev => {
          const next = [...prev];
          next[i] = { fileName: arr[i].name, status: 'done' };
          return next;
        });
      } catch (err) {
        setUploading(prev => {
          const next = [...prev];
          next[i] = { fileName: arr[i].name, status: 'error', error: err?.response?.data?.error || err.message };
          return next;
        });
      }
    }
    await refresh();
    // Auto-clear the upload tray after a couple seconds
    setTimeout(() => setUploading([]), 3000);
  }

  async function handlePasteSubmit() {
    if (!pasteText.trim() || !pasteTitle.trim()) return;
    const blob = new Blob([pasteText], { type: 'text/plain' });
    const file = new File([blob], `${pasteTitle.replace(/[^a-z0-9._-]+/gi, '_')}.txt`, { type: 'text/plain' });
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', pasteTitle);
    fd.append('fileType', 'email_text');
    if (scopeSiteId) fd.append('siteId', scopeSiteId);
    try {
      await historyApi.uploadDoc(fd);
      setPasteText(''); setPasteTitle(''); setPasteOpen(false);
      await refresh();
    } catch (err) {
      alert('Upload failed: ' + (err?.response?.data?.error || err.message));
    }
  }

  async function handleReprocess(id) {
    try { await historyApi.reprocessDoc(id); await refresh(); }
    catch (e) { alert('Reprocess failed: ' + (e?.response?.data?.error || e.message)); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this document? Indexed chunks will be removed; KB entries promoted from it will lose their source link.')) return;
    try { await historyApi.deleteDoc(id); await refresh(); }
    catch (e) { alert('Delete failed: ' + (e?.response?.data?.error || e.message)); }
  }

  return (
    <div style={{ padding: 24, color: C.textPrimary, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <FileText size={22} color={C.blue} />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>History Repository — Upload Manager</h2>
      </div>

      {/* Site scope picker */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: C.textMuted }}>Scope new uploads to site (optional):</span>
        <select value={scopeSiteId} onChange={e => setScopeSiteId(e.target.value)}
          style={{ padding: '6px 10px', background: C.surface, border: `1px solid ${C.border}`,
                   borderRadius: 6, color: C.textPrimary, fontSize: 12 }}>
          <option value="">— Tenant-wide —</option>
          {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault(); setDragOver(false);
          const files = [...e.dataTransfer.files].filter(f =>
            ACCEPTED_EXT.some(ext => f.name.toLowerCase().endsWith(ext))
          );
          if (files.length) handleFiles(files);
        }}
        onClick={() => fileInput.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? C.blue : C.border}`,
          borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer',
          background: dragOver ? C.blue + '11' : C.surface,
          transition: 'all 0.15s',
        }}>
        <Upload size={32} color={C.blue} style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>Drop PDF or Word files here, or click to browse</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
          Max 10 MB per file. Bulk upload supported.
        </div>
        <input ref={fileInput} type="file" multiple accept=".pdf,.docx" style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
      </div>

      <div style={{ marginTop: 12 }}>
        <button onClick={() => setPasteOpen(o => !o)}
          style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
                   background: 'transparent', color: C.textSecondary, fontSize: 12, fontWeight: 600,
                   cursor: 'pointer', fontFamily: 'inherit' }}>
          {pasteOpen ? 'Cancel paste' : '+ Paste email or text content'}
        </button>
      </div>

      {pasteOpen && (
        <div style={{ marginTop: 12, background: C.surfaceElevated, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: 16 }}>
          <input type="text" placeholder="Title (e.g. 'Subject: Boiler shutdown')"
            value={pasteTitle} onChange={e => setPasteTitle(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                     background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
                     color: C.textPrimary, fontSize: 13, marginBottom: 8 }} />
          <textarea rows={8} placeholder="Paste email body or any text here…"
            value={pasteText} onChange={e => setPasteText(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                     background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
                     color: C.textPrimary, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
          <button onClick={handlePasteSubmit}
            disabled={!pasteText.trim() || !pasteTitle.trim()}
            style={{ marginTop: 8, padding: '8px 14px', borderRadius: 8, border: 'none',
                     background: pasteText.trim() && pasteTitle.trim() ? C.blue : C.surface,
                     color: pasteText.trim() && pasteTitle.trim() ? '#fff' : C.textMuted,
                     fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Upload as email_text
          </button>
        </div>
      )}

      {/* Upload tray */}
      {uploading.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>Uploading</div>
          {uploading.map((u, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
              <span>{u.fileName}</span>
              <span style={{ color: u.status === 'error' ? C.red : u.status === 'done' ? C.green : C.textMuted }}>
                {u.status === 'error' ? `Error: ${u.error}` : u.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Doc list */}
      <div style={{ marginTop: 28 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase',
                     letterSpacing: '0.06em', marginBottom: 10 }}>
          Indexed documents ({docs.length})
        </h3>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          {docs.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
              No documents uploaded yet.
            </div>
          ) : docs.map(d => (
            <div key={d.id} style={{
              padding: '12px 16px', display: 'grid',
              gridTemplateColumns: '1fr auto auto auto', alignItems: 'center', gap: 16,
              borderTop: `1px solid ${C.border}`,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{d.title}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                  {d.fileName} · {d.fileType.toUpperCase()} · {(d.fileSizeBytes / 1024).toFixed(0)} KB ·
                  {d.chunkCount > 0 ? ` ${d.chunkCount} chunks` : ''} · uploaded {new Date(d.uploadedAt).toLocaleString()}
                </div>
                {d.status === 'FAILED' && d.errorMessage && (
                  <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{d.errorMessage}</div>
                )}
              </div>
              <div>{statusChip(d.status)}</div>
              <div>
                {(d.status === 'FAILED' || d.status === 'INDEXED') && (
                  <button onClick={() => handleReprocess(d.id)} title="Re-process"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}>
                    <RotateCw size={14} />
                  </button>
                )}
              </div>
              <div>
                <button onClick={() => handleDelete(d.id)} title="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
